'use client'

/*
 * NBackTest
 *
 * A square lights up on a 3x3 grid every 2.5 seconds. Press Match when it is
 * in the same place as it was n squares ago. Clear a level and n goes up.
 *
 * FORMAT. Single n-back on position only, the task Kirchner introduced in
 * 1958. The dual version made famous by Jaeggi and colleagues in 2008 adds a
 * second stream of spoken letters, which needs reliable audio timing that
 * browsers do not give, so this version does not pretend to be it.
 *
 * LEVELS AND LIVES. Each block is 20 scored squares plus the n needed to get
 * started, with exactly 6 matches. Three errors or fewer (misses plus false
 * alarms) clears the level, which is the standard advance rule from the
 * training literature, give or take one. A failed block costs one of two
 * lives and replays the same level, the same shape as the visual memory test.
 *
 * FALSE ALARMS COUNT. Without them, pressing Match on every square would catch
 * every target and clear every level.
 *
 * ABOUT THE COMPARISON. No normative sample exists for this format: pace, grid
 * size, block length and the advance rule all vary between implementations.
 * The labels describe the level and make no population claim, as on the
 * mental rotation test.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { Layers, RotateCcw, ArrowRight, Heart } from 'lucide-react'
import TestPromptPopup from '@/components/TestPromptPopup'

const CELLS = 9
const SCORED_PER_BLOCK = 20
const TARGETS_PER_BLOCK = 6
const MAX_ERRORS_TO_PASS = 3
const LIVES = 2
const SHOW_MS = 500
const TRIAL_MS = 2500

const TIERS = [
  { min: 5, label: 'Exceptional', sub: 'Five steps back is rare air', ring: 'from-fuchsia-500 to-purple-600' },
  { min: 4, label: 'Sharp', sub: 'Holding four positions while they move', ring: 'from-cyan-500 to-blue-600' },
  { min: 3, label: 'Strong', sub: 'Three back is where most people start to strain', ring: 'from-emerald-500 to-teal-600' },
  { min: 2, label: 'Solid', sub: 'Two back is the level most people can hold', ring: 'from-indigo-500 to-violet-600' },
  { min: 1, label: 'Getting started', sub: 'Two back is next, and it is a real step up', ring: 'from-amber-500 to-orange-600' },
  { min: 0, label: 'Warming up', sub: 'The pace takes a round to get used to', ring: 'from-rose-500 to-pink-600' },
]
const tierFor = (level) => TIERS.find((t) => level >= t.min)

const randomInt = (n) => Math.floor(Math.random() * n)

// Exactly TARGETS_PER_BLOCK matches, and no accidental ones: every non-target
// square is kept away from the position n steps back.
function buildBlock(n) {
  const length = SCORED_PER_BLOCK + n
  const targetIdx = new Set()
  while (targetIdx.size < TARGETS_PER_BLOCK) targetIdx.add(n + randomInt(SCORED_PER_BLOCK))

  const positions = []
  for (let i = 0; i < length; i++) {
    if (targetIdx.has(i)) {
      positions.push(positions[i - n])
    } else {
      let p
      do { p = randomInt(CELLS) } while ((i >= n && p === positions[i - n]) || p === positions[i - 1])
      positions.push(p)
    }
  }
  return positions.map((pos, i) => ({ pos, target: targetIdx.has(i) }))
}

function scoreBlock(trials, responses, n) {
  let hits = 0
  let misses = 0
  let falseAlarms = 0
  trials.forEach((t, i) => {
    if (i < n) return
    if (t.target && responses[i]) hits++
    else if (t.target) misses++
    else if (responses[i]) falseAlarms++
  })
  return { hits, misses, falseAlarms, errors: misses + falseAlarms }
}

export default function NBackTest() {
  const [phase, setPhase] = useState('idle') // idle | playing | between | finished
  const [level, setLevel] = useState(1)
  const [lives, setLives] = useState(LIVES)
  const [trialIndex, setTrialIndex] = useState(0)
  const [lit, setLit] = useState(null)
  const [flash, setFlash] = useState(null) // hit | falseAlarm | miss
  const [lastBlock, setLastBlock] = useState(null)
  const [cleared, setCleared] = useState(0)
  const [best, setBest] = useState(null)
  const [isNewBest, setIsNewBest] = useState(false)
  const [countUp, setCountUp] = useState(0)

  const block = useRef([])
  const responses = useRef([])
  const indexRef = useRef(0)
  const levelRef = useRef(1)
  const livesRef = useRef(LIVES)
  const clearedRef = useRef(0)
  const bestRef = useRef(null)
  const rootRef = useRef(null)

  const timers = useRef([])
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  useEffect(() => clearTimers, [])

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('biqt_nback_best')
      const parsed = stored === null ? null : parseInt(stored, 10)
      bestRef.current = Number.isFinite(parsed) ? parsed : null
      setBest(bestRef.current)
    } catch { setBest(null) }
  }, [])

  const flashFor = (kind) => {
    setFlash(kind)
    timers.current.push(setTimeout(() => setFlash(null), 350))
  }

  const finish = useCallback(() => {
    clearTimers()
    setLit(null)
    setPhase('finished')
    const reached = clearedRef.current
    if (bestRef.current === null || reached > bestRef.current) {
      setIsNewBest(bestRef.current !== null)
      bestRef.current = reached
      setBest(reached)
      try { window.localStorage.setItem('biqt_nback_best', String(reached)) } catch {}
    }
  }, [])

  const endBlock = useCallback(() => {
    const n = levelRef.current
    const result = scoreBlock(block.current, responses.current, n)
    const passed = result.errors <= MAX_ERRORS_TO_PASS
    setLastBlock({ ...result, n, passed })

    if (passed) {
      clearedRef.current = n
      setCleared(n)
      levelRef.current = n + 1
      setLevel(n + 1)
      setPhase('between')
      return
    }

    livesRef.current -= 1
    setLives(livesRef.current)
    if (livesRef.current <= 0) finish()
    else setPhase('between')
  }, [finish])

  const runTrial = useCallback((i) => {
    indexRef.current = i
    setTrialIndex(i)
    setLit(block.current[i].pos)
    timers.current.push(setTimeout(() => setLit(null), SHOW_MS))
    timers.current.push(setTimeout(() => {
      const n = levelRef.current
      if (i >= n && block.current[i].target && !responses.current[i]) flashFor('miss')
      if (i + 1 < block.current.length) runTrial(i + 1)
      else endBlock()
    }, TRIAL_MS))
  }, [endBlock])

  const startBlock = useCallback(() => {
    clearTimers()
    block.current = buildBlock(levelRef.current)
    responses.current = []
    indexRef.current = 0
    setTrialIndex(0)
    setLit(null)
    setFlash(null)
    setPhase('playing')
    // Keep the grid and the Match button on screen on a phone. Scrolled by
    // hand because scrollIntoView parks the top under the sticky nav.
    const top = rootRef.current?.getBoundingClientRect().top
    if (top !== undefined) window.scrollTo({ top: window.scrollY + top - 80, behavior: 'smooth' })
    // A beat before the first square so the level change can register.
    timers.current.push(setTimeout(() => runTrial(0), 700))
  }, [runTrial])

  const start = () => {
    levelRef.current = 1
    livesRef.current = LIVES
    clearedRef.current = 0
    setLevel(1)
    setLives(LIVES)
    setCleared(0)
    setLastBlock(null)
    setIsNewBest(false)
    startBlock()
  }

  const respond = useCallback(() => {
    if (phase !== 'playing') return
    const i = indexRef.current
    if (responses.current[i] || i < levelRef.current) return
    responses.current[i] = true
    flashFor(block.current[i]?.target ? 'hit' : 'falseAlarm')
  }, [phase])

  useEffect(() => {
    if (phase !== 'playing') return
    const onKey = (e) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === ' ' || e.key.toLowerCase() === 'm') { e.preventDefault(); respond() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, respond])

  useEffect(() => {
    if (phase !== 'finished') { setCountUp(0); return }
    let frame
    const started = performance.now()
    const tick = (now) => {
      const t = Math.min(1, (now - started) / 650)
      setCountUp(Math.round((1 - Math.pow(1 - t, 3)) * cleared))
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [phase, cleared])

  const blockLength = SCORED_PER_BLOCK + level
  const tier = tierFor(cleared)
  const instruction = level === 1
    ? 'Press Match when the square is where it was one step ago'
    : `Press Match when the square is where it was ${level} steps ago`

  return (
    <div ref={rootRef} className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
      {phase !== 'finished' ? (
        <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 px-6 sm:px-8 py-8">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-xs font-semibold text-blue-200 uppercase tracking-wide">Level</p>
              <p className="text-3xl font-bold text-white tabular-nums leading-none">{level}-back</p>
            </div>
            <p className={`text-sm font-semibold min-h-5 transition-colors ${
              flash === 'miss' ? 'text-amber-200' : 'text-white/90'
            }`}>
              {phase === 'idle' && 'Ready when you are'}
              {phase === 'playing' && (flash === 'miss' ? 'Missed a match' : `${Math.min(trialIndex + 1, blockLength)} of ${blockLength}`)}
              {phase === 'between' && (lastBlock?.passed ? 'Level cleared' : 'Not this time')}
            </p>
            <div className="flex gap-1">
              {Array.from({ length: LIVES }).map((_, i) => (
                <Heart key={i} className={`w-5 h-5 transition-all ${i < lives ? 'text-rose-400 fill-rose-400' : 'text-white/25'}`} />
              ))}
            </div>
          </div>

          {phase === 'between' ? (
            <div className="mx-auto w-full max-w-sm aspect-square rounded-2xl bg-white/10 flex flex-col items-center justify-center text-center px-6">
              <p className="text-5xl font-bold text-white tabular-nums">
                {lastBlock.errors}
                <span className="text-xl font-semibold text-white/70 ml-1">
                  {lastBlock.errors === 1 ? 'error' : 'errors'}
                </span>
              </p>
              <p className="text-white/80 text-sm mt-2 tabular-nums">
                {lastBlock.hits} of {TARGETS_PER_BLOCK} matches caught · {lastBlock.falseAlarms} false {lastBlock.falseAlarms === 1 ? 'alarm' : 'alarms'}
              </p>
              <p className="text-white font-semibold mt-5 leading-relaxed">
                {lastBlock.passed
                  ? `Next: ${level}-back. Match the square from ${level} steps ago.`
                  : `${MAX_ERRORS_TO_PASS} or fewer errors clears a level. ${lives} ${lives === 1 ? 'life' : 'lives'} left for ${level}-back.`}
              </p>
              <button
                type="button"
                onClick={startBlock}
                className="mt-6 inline-flex items-center bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-3 rounded-2xl shadow-lg transition-all"
              >
                {lastBlock.passed ? `Start ${level}-back` : 'Try again'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 mx-auto w-full max-w-sm aspect-square">
              {Array.from({ length: CELLS }).map((_, i) => (
                <div
                  key={i}
                  className={`rounded-xl transition-colors duration-100 ${
                    lit === i ? 'bg-white shadow-lg' : 'bg-white/15'
                  }`}
                />
              ))}
            </div>
          )}

          {phase === 'idle' && (
            <div className="text-center mt-7">
              <button
                type="button"
                onClick={start}
                className="inline-flex items-center bg-white text-blue-700 hover:bg-blue-50 font-bold text-lg px-10 py-4 rounded-2xl shadow-lg transition-all"
              >
                <Layers className="w-5 h-5 mr-2" />
                Start
              </button>
              <p className="text-blue-100 text-sm mt-4 max-w-sm mx-auto leading-relaxed">
                A square lights up every 2.5 seconds. Press Match when it lands
                where it was n squares ago. You start at 1-back, the one just
                before. {MAX_ERRORS_TO_PASS} errors or fewer clears a level. Space
                or M works too.
              </p>
            </div>
          )}

          {phase === 'playing' && (
            <div className="mt-6 max-w-sm mx-auto">
              <button
                type="button"
                onPointerDown={(e) => { e.preventDefault(); respond() }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); respond() } }}
                className={`relative w-full h-16 rounded-xl font-bold text-lg shadow-md active:scale-[0.98] transition-all touch-manipulation ${
                  flash === 'hit' ? 'bg-emerald-400 text-gray-900' : flash === 'falseAlarm' ? 'bg-rose-400 text-gray-900' : 'bg-white text-blue-700'
                }`}
              >
                Match
                <span className="hidden sm:block absolute top-1 right-3 text-[10px] font-semibold text-gray-900/40 uppercase">space</span>
              </button>
              <p className="text-blue-100 text-xs text-center mt-3 leading-relaxed">{instruction}</p>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className={`bg-gradient-to-br ${tier.ring} px-6 sm:px-8 py-10 text-center`}>
            {isNewBest && (
              <span className="inline-block px-3 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-xs font-bold tracking-wide mb-4">
                NEW PERSONAL BEST
              </span>
            )}
            <p className="text-xs font-semibold text-white/70 uppercase tracking-[0.2em] mb-2">Highest level cleared</p>
            <p className="text-8xl sm:text-9xl font-bold text-white tabular-nums leading-none drop-shadow-sm">
              {countUp}
              {/* "0-back" is not a level, so a run that clears nothing reads as levels. */}
              <span className={`text-3xl sm:text-4xl font-bold text-white/80 ${cleared > 0 ? '' : 'ml-2'}`}>{cleared > 0 ? '-back' : 'levels'}</span>
            </p>
            <p className="text-3xl sm:text-4xl font-bold text-white mt-4 tracking-tight">{tier.label}</p>
            <p className="text-white/80 mt-1">{tier.sub}</p>
            {lastBlock && (
              <p className="text-white/80 text-xs mt-3 tabular-nums">
                Last round, {lastBlock.n}-back: {lastBlock.hits} of {TARGETS_PER_BLOCK} matches caught, {lastBlock.falseAlarms} false {lastBlock.falseAlarms === 1 ? 'alarm' : 'alarms'}
              </p>
            )}
          </div>

          <div className="p-6 sm:p-8">
            <Link
              href="/test"
              className="group relative block w-full overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-[length:200%_100%] hover:bg-[position:100%_0] p-[3px] shadow-xl shadow-purple-500/25 transition-[background-position] duration-700"
            >
              <span className="block rounded-[13px] bg-white px-6 py-5 text-center transition-colors group-hover:bg-transparent">
                <span className="block text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent group-hover:text-white transition-colors">
                  Now find your actual IQ
                </span>
                <span className="mt-1 flex items-center justify-center text-sm font-medium text-gray-500 group-hover:text-blue-100 transition-colors">
                  50 questions · all four domains · instant score
                  <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                </span>
              </span>
            </Link>

            <p className="text-sm text-gray-600 text-center leading-relaxed mt-5">
              This is working memory updating: holding a moving sequence, comparing
              and discarding. It relates to reasoning, and practising it makes you
              better at n-back rather than smarter.
            </p>

            <div className="flex items-center justify-center gap-5 mt-5">
              <button
                type="button"
                onClick={start}
                className="inline-flex items-center text-gray-600 hover:text-gray-900 font-semibold text-sm transition-colors"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Play again
              </button>
              {best !== null && (
                <span className="text-sm text-gray-400 tabular-nums">Best: {best}-back</span>
              )}
            </div>

            <p className="text-xs text-gray-400 mt-5 text-center leading-relaxed">
              There is no normative sample for this format. Pace, grid size and
              the rules for clearing a level all vary between n-back versions, so
              the labels describe your level rather than rank it.
            </p>
          </div>
        </>
      )}

      {phase === 'finished' && (
        <TestPromptPopup
          delayMs={4000}
          storageKey="biqt_nback_prompt"
          headline="Working memory measured. Now the reasoning."
          body={
            <>
              You just measured working memory updating. Our 50-question
              assessment covers verbal, numerical and spatial reasoning as well,
              and gives you a full-scale score with your exact percentile.
            </>
          }
        />
      )}
    </div>
  )
}
